// @ts-nocheck
import { useMemo } from 'react';
import {
  Trash2,
  Lock,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  FileText,
  GripVertical,
} from 'lucide-react';
import Tooltip from '@mui/material/Tooltip';
import _ from 'lodash';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import RegisterFormPreview from './RegisterFormPreview';
import './BrowserPreview.css';

/* eslint-disable react/prop-types */

function getFieldDndId(field, index) {
  return field?.fieldId || `__field-${index}`;
}

/**
 * CreateProfileFieldContent — replica of the PUBLIC /create-profile field
 * rendering: apps/app's `OrgProfileForm` (label + .cp-input) +
 * `ProfileFieldInput` (per-type inputs, incl. year/phone). Keep in sync
 * with those files — this is what "Live Preview" promises the admin.
 */
function CreateProfileFieldContent({ field }) {
  const placeholder = field.helperText || '';
  const currentYear = new Date().getFullYear();
  const input = (() => {
    if (field.fieldType === 'date') {
      return <input type="date" className="cpp-input" disabled />;
    }
    if (field.fieldType === 'year') {
      const years = [];
      for (let y = currentYear; y >= currentYear - 8; y--) years.push(y);
      return (
        <select className="cpp-input" disabled>
          <option value="">Select…</option>
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      );
    }
    if (field.fieldType === 'phone') {
      return <input type="tel" className="cpp-input" placeholder={placeholder || '(XXX) XXX-XXXX'} disabled />;
    }
    if (field.fieldType === 'select' && Array.isArray(field.options) && field.options.length) {
      return (
        <select className="cpp-input" disabled>
          <option value="">Select…</option>
          {field.options.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      );
    }
    return <input type="text" className="cpp-input" placeholder={placeholder} disabled />;
  })();

  return (
    <div className="cpp-field">
      <label className="cpp-label">
        {field.name}
        {field.required && <span className="cpp-required">*</span>}
      </label>
      {input}
      {field.helperText && <span className="cpp-helper">{field.helperText}</span>}
    </div>
  );
}

/**
 * FieldPreview - Static (non-sortable) row. Used when no onReorderFields prop
 * is provided. Rows only render for the create-profile variant now — the
 * register variant is fully self-contained in RegisterFormPreview, and the
 * legacy no-variant fieldset path was deleted with it (zero consumers).
 */
function FieldPreview({ field, index, onRemove }) {
  return (
    <div className="preview-field-wrapper">
      <CreateProfileFieldContent field={field} />

      {!field.permanent && onRemove && (
        <Tooltip title={`Remove "${field.name}" field`}>
          <button
            className="preview-remove-btn"
            onClick={() => onRemove(index)}
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </Tooltip>
      )}
    </div>
  );
}

/**
 * SortableFieldPreview - Drag-and-drop row. Used when onReorderFields is provided.
 */
function SortableFieldPreview({ field, index, dndId, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: dndId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`preview-field-wrapper preview-field-wrapper--sortable${isDragging ? ' preview-field-wrapper--dragging' : ''}`}
    >
      <button
        type="button"
        className="preview-drag-handle"
        aria-label={`Reorder ${field.name || 'field'}`}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="w-4 h-4" />
      </button>

      <CreateProfileFieldContent field={field} />

      {!field.permanent && onRemove && (
        <Tooltip title={`Remove "${field.name}" field`}>
          <button
            className="preview-remove-btn"
            onClick={() => onRemove(index)}
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </Tooltip>
      )}
    </div>
  );
}

/**
 * BrowserPreview - A browser-style preview component showing how fields will appear to users
 *
 * @param {Object} props
 * @param {Array} props.fields - Array of field objects to display
 * @param {Function} props.onRemoveField - Callback when a field is removed
 * @param {Function} [props.onReorderFields] - Callback when fields are reordered via drag-and-drop.
 *   Receives the new fields array. When provided, the preview becomes a sortable list with drag handles.
 * @param {string} props.url - URL to display in the browser bar (default: yourleague.com/register)
 * @param {string} props.title - Title for the preview section (default: "Player #1")
 * @param {string} props.emptyMessage - Message to show when no fields (default: "No fields configured yet...")
 * @param {string} props.type - Type of preview: "player" or "team" (default: "player")
 * @param {string} props.variant - Which public page the content replicates:
 *   "create-profile" — the PUBLIC /create-profile page (apps/app
 *   CreateProfilePage + OrgProfileForm): centered 480px card, label +
 *   .cp-input fields, dark "Save & Continue" button; rows keep the admin's
 *   drag/remove affordances.
 *   "register" — the PUBLIC registration funnel (see RegisterFormPreview):
 *   team step or players step per `type`, fully inert.
 *   (The old no-variant legacy fieldset style is deleted — it stopped having
 *   consumers when FormFieldsSection moved to "register".)
 * @param {Object} [props.register] - Extra context for variant="register":
 *   { sport, registrationName, excludeTeamName, minPlayers, maxPlayers }.
 */
export default function BrowserPreview({
  fields = [],
  onRemoveField,
  onReorderFields,
  url = "yourleague.com/register",
  title = "Player #1",
  emptyMessage = "No fields configured yet. Add fields above to see them here.",
  type = "player",
  variant,
  register
}) {
  const sortable = typeof onReorderFields === 'function';

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Partition fields into pinned (immovable, rendered statically at the top)
  // and sortable. Pinned only applies when DnD is enabled; static callers
  // render every field through the existing path so visuals stay unchanged.
  const pinnedFields = sortable ? _.filter(fields, (f) => f.pinned) : [];
  const sortableFields = sortable ? _.filter(fields, (f) => !f.pinned) : fields;

  const sortableIds = useMemo(
    () => sortableFields.map((f, i) => getFieldDndId(f, i)),
    [sortableFields]
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = sortableIds.indexOf(active.id);
    const newIndex = sortableIds.indexOf(over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const reordered = arrayMove(sortableFields, oldIndex, newIndex);
    onReorderFields([...pinnedFields, ...reordered]);
  };

  // Pinned rows occupy [0..pinnedFields.length); sortable rows follow.
  // We thread the full-array index into onRemove so the caller's
  // splice(index, 1) targets the right entry.
  const fullIndexOfSortable = (i) => pinnedFields.length + i;

  const renderFields = () => {
    const pinnedNodes = pinnedFields.map((field, i) => (
      <FieldPreview
        key={getFieldDndId(field, i)}
        field={field}
        index={i}
        onRemove={onRemoveField}
      />
    ));

    if (sortable) {
      return (
        <>
          {pinnedNodes}
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
              {sortableFields.map((field, i) => (
                <SortableFieldPreview
                  key={sortableIds[i]}
                  dndId={sortableIds[i]}
                  field={field}
                  index={fullIndexOfSortable(i)}
                  onRemove={onRemoveField}
                />
              ))}
            </SortableContext>
          </DndContext>
        </>
      );
    }
    return _.map(sortableFields, (field, index) => (
      <FieldPreview
        key={index}
        field={field}
        index={index}
        onRemove={onRemoveField}
      />
    ));
  };

  return (
    <div className="browser-preview">
      {/* Browser Chrome */}
      <div className="browser-chrome">
        <div className="browser-buttons">
          <span className="browser-btn browser-btn-close"></span>
          <span className="browser-btn browser-btn-minimize"></span>
          <span className="browser-btn browser-btn-maximize"></span>
        </div>

        <div className="browser-nav">
          <button className="browser-nav-btn" disabled>
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button className="browser-nav-btn" disabled>
            <ChevronRight className="w-4 h-4" />
          </button>
          <button className="browser-nav-btn" disabled>
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        <div className="browser-url-bar">
          <Lock className="w-4 h-4 browser-url-secure" />
          <span className="browser-url-text">{url}</span>
        </div>

        <div className="live-indicator">
          <span className="live-dot"></span>
          Live Preview
        </div>
      </div>

      {/* Browser Content */}
      <div className="browser-content">
        {variant === 'create-profile' ? (
          /* Replica of the public /create-profile page: centered card on a
             soft ground (apps/app CreateProfilePage), fields per
             OrgProfileForm, inert dark submit button. */
          <div className="cpp-page">
            {fields.length > 0 ? (
              <div className="cpp-card">
                <h1 className="cpp-title">{title}</h1>
                <p className="cpp-subtitle">Please fill out your profile before continuing.</p>
                <div className="cpp-form">
                  {renderFields()}
                  <button type="button" className="cpp-submit" disabled>
                    Save &amp; Continue
                  </button>
                </div>
              </div>
            ) : (
              <div className="preview-empty-state">
                <FileText />
                <p>{emptyMessage}</p>
              </div>
            )}
          </div>
        ) : (
          /* Replica of the public registration funnel (team or players step
             per `type`); fully inert, self-contained. */
          <RegisterFormPreview
            fields={fields}
            type={type}
            emptyMessage={emptyMessage}
            {...(register || {})}
          />
        )}
      </div>
    </div>
  );
}
